import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { MENU_CONFIG } from '../../lib/config/menuConfig';
import { IconRenderer } from '../../lib/utils/iconMapper';

/**
 * Replica statica e semplificata della sidebar del CRM per lo step Moduli:
 * legge sezioni e voci REALI da MENU_CONFIG e usa gli stessi stili della
 * sidebar vera (Layout.tsx): header sezione con chevron rosa + label in
 * gradiente #E91E8C→#F97316, voci con icona lucide 15px. Le sezioni in
 * `expandedIds` sono aperte (con animazione), le altre collassate e attenuate.
 * Solo visuale: nessuna navigazione.
 */
export function SidebarPreview({ expandedIds }: { expandedIds: Set<string> }) {
  const sections = MENU_CONFIG.filter(i => !i.hidden && i.children && i.children.length > 0);
  const flat = MENU_CONFIG.filter(i => !i.hidden && (!i.children || i.children.length === 0));

  const itemRow = (id: string, label: string, icon?: string) => (
    <div key={id} className="flex items-center px-3 py-1.5 text-sm rounded-md text-[#37352F]">
      {icon && (
        <IconRenderer
          iconName={icon}
          iconLibrary="lucide"
          className="mr-2.5 flex-shrink-0"
          size={15}
          style={{ width: 15, height: 15 }}
        />
      )}
      <span className="flex-1">{label}</span>
    </div>
  );

  return (
    <div className="w-full bg-white border border-r-0 border-[rgba(55,53,47,0.12)] rounded-l-xl shadow-[0_8px_24px_rgba(55,53,47,0.08)] overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2.5 bg-[#FBFBFA] border-b border-[rgba(55,53,47,0.07)]">
        <img src="/logo_colorato.png" alt="Flusia" className="h-4 w-auto" />
        <span className="text-[11px] text-[#787774]">La tua sidebar</span>
      </div>
      <div className="bg-[#FBFBFA] px-2 py-2 max-h-[62vh] overflow-y-auto">
        {flat.map(item => itemRow(item.id, item.label, item.icon))}
        {sections.map(section => {
          const expanded = expandedIds.has(section.id);
          return (
            <div key={section.id} className="mb-2 mt-4 first:mt-0">
              <div className="w-full flex items-center gap-1 px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded-md">
                <ChevronDown
                  className={`h-2.5 w-2.5 transition-transform duration-150 ${expanded ? 'text-[#E91E8C]' : '-rotate-90 text-[#C9C7C3]'}`}
                />
                <span
                  className={
                    expanded
                      ? 'flusia-title bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent'
                      : 'text-[#C9C7C3]'
                  }
                >
                  {section.label}
                </span>
              </div>
              <AnimatePresence initial={false}>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden mt-0.5 space-y-0.5"
                  >
                    {(section.children || []).filter(c => !c.hidden).map(child =>
                      itemRow(child.id, child.label, child.icon)
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
